import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import TasksAPI from '../../api/tasks';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getTasks(_state) {
    return _state.records;
  },
  getTasksByEntity: _state => (taskableType, taskableId) => {
    return _state.records.filter(
      task =>
        task.taskable_type === taskableType && task.taskable_id === taskableId
    );
  },
  getOverdueTasks(_state) {
    const now = new Date();
    return _state.records.filter(
      task =>
        task.due_date &&
        new Date(task.due_date) < now &&
        !['completed', 'cancelled'].includes(task.status)
    );
  },
  getMyTasks: _state => userId => {
    return _state.records.filter(task => task.assignee_id === userId);
  },
  getPendingTasks(_state) {
    return _state.records.filter(
      task => !['completed', 'cancelled'].includes(task.status)
    );
  },
};

export const actions = {
  get: async function getTasks({ commit }, params = {}) {
    commit(types.SET_TASK_UI_FLAG, { isFetching: true });
    try {
      const response = await TasksAPI.get(params);
      commit(types.SET_TASKS, response.data.payload);
      return response.data;
    } catch {
      return undefined;
    } finally {
      commit(types.SET_TASK_UI_FLAG, { isFetching: false });
    }
  },
  getForEntity: async function getTasksForEntity(
    { commit },
    { taskableType, taskableId, params = {} }
  ) {
    commit(types.SET_TASK_UI_FLAG, { isFetching: true });
    try {
      const response = await TasksAPI.getForEntity(
        taskableType,
        taskableId,
        params
      );
      commit(types.SET_TASKS, response.data.payload);
      return response.data;
    } catch {
      return undefined;
    } finally {
      commit(types.SET_TASK_UI_FLAG, { isFetching: false });
    }
  },
  show: async function showTask({ commit, state: _state }, { id }) {
    commit(types.SET_TASK_UI_FLAG, { isFetching: true });
    try {
      const response = await TasksAPI.show(id);
      const exists = _state.records.some(r => r.id === response.data.id);
      if (exists) {
        commit(types.EDIT_TASK, response.data);
      } else {
        commit(types.ADD_TASK, response.data);
      }
      return response.data;
    } catch {
      return undefined;
    } finally {
      commit(types.SET_TASK_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createTask({ commit }, data) {
    commit(types.SET_TASK_UI_FLAG, { isCreating: true });
    try {
      const response = await TasksAPI.create(data);
      commit(types.ADD_TASK, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TASK_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_TASK_UI_FLAG, { isUpdating: true });
    try {
      const response = await TasksAPI.update(id, updateObj);
      commit(types.EDIT_TASK, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TASK_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_TASK_UI_FLAG, { isDeleting: true });
    try {
      await TasksAPI.destroy(id);
      commit(types.DELETE_TASK, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TASK_UI_FLAG, { isDeleting: false });
    }
  },
  complete: async ({ commit }, id) => {
    commit(types.SET_TASK_UI_FLAG, { isUpdating: true });
    try {
      const response = await TasksAPI.complete(id);
      commit(types.EDIT_TASK, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TASK_UI_FLAG, { isUpdating: false });
    }
  },
  reopen: async ({ commit }, id) => {
    commit(types.SET_TASK_UI_FLAG, { isUpdating: true });
    try {
      const response = await TasksAPI.reopen(id);
      commit(types.EDIT_TASK, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TASK_UI_FLAG, { isUpdating: false });
    }
  },
};

export const mutations = {
  [types.SET_TASK_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.ADD_TASK]: MutationHelpers.create,
  [types.SET_TASKS]: MutationHelpers.set,
  [types.EDIT_TASK]: MutationHelpers.updateAttributes,
  [types.DELETE_TASK]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
